/**
 * Decision Approval Card - Consultant View
 * 
 * Shows pending decision approval request with context
 * and allows consultant to approve or deny with notes.
 * 
 * @version 1.0.0
 * @date 2026-01-29
 */

import { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Clock, Building2, FileText, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { EducationDrawer } from '../education/EducationDrawer';
import { loadEducationTopic } from '../../utils/educationTopics';
// Import the registry to lookup education topic IDs - in a real app this would come from the API/DecisionNode
import { ATLANSSIAN_SERVICE_DESK_COUNT_NODE } from '../../config/decision-nodes/atlassian-service-desk-count';
import { useEducationPreferences } from '../../hooks/useEducationPreferences';

interface PendingDecision {
  historyId: string;
  companyId: string;
  companyName: string;
  engagementId?: string;
  engagementName?: string;
  decisionNodeId: string;
  decisionTitle: string;
  severity: 'warning' | 'block';
  selectedOption: string;
  overrideReason?: string;
  evaluatedAt: string;
  evaluatedBy: string;
  confidence?: 'high' | 'medium' | 'low';
}

interface DecisionApprovalCardProps {
  decision: PendingDecision;
  onApprove: (decisionId: string, notes: string) => Promise<void>;
  onDeny: (decisionId: string, notes: string) => Promise<void>;
  onViewDetails: (decision: PendingDecision) => void;
  userId?: string;
  accessToken?: string;
}

export function DecisionApprovalCard({
  decision,
  onApprove,
  onDeny,
  onViewDetails,
  userId,
  accessToken,
}: DecisionApprovalCardProps) {
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [showDenyForm, setShowDenyForm] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [denyNotes, setDenyNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEducation, setShowEducation] = useState(false);

  // In a real app, the pending decision would include the educationTopicId
  // For this pilot, we can lookup based on the node ID
  const educationTopicId = decision.decisionNodeId === ATLANSSIAN_SERVICE_DESK_COUNT_NODE.id 
    ? ATLANSSIAN_SERVICE_DESK_COUNT_NODE.educationTopicId 
    : undefined;
    
  const educationTopic = educationTopicId ? loadEducationTopic(educationTopicId) : null;

  // Preferences hook
  const { dismissTopic } = useEducationPreferences(userId || '', accessToken || '');

  const handleApprove = async () => {
    if (!approveNotes.trim()) {
      toast.error('Please provide approval notes');
      return;
    }

    setLoading(true);
    try {
      await onApprove(decision.historyId, approveNotes);
      toast.success('Decision approved');
      setShowApproveForm(false);
      setApproveNotes('');
    } catch (error) {
      console.error('Error approving decision:', error);
      toast.error('Failed to approve decision');
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!denyNotes.trim()) {
      toast.error('Please provide denial reason');
      return;
    }

    setLoading(true);
    try {
      await onDeny(decision.historyId, denyNotes);
      toast.success('Decision denied');
      setShowDenyForm(false);
      setDenyNotes('');
    } catch (error) {
      console.error('Error denying decision:', error);
      toast.error('Failed to deny decision');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getConfidenceConfig = () => {
    // Default to medium if undefined (fallback)
    const confidence = decision.confidence || 'medium';
    
    switch (confidence) {
      case 'high':
        return { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'High Confidence' };
      case 'medium':
        return { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Medium Confidence' };
      case 'low':
        return { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Low Confidence' };
      default:
        return { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Confidence' };
    }
  };
  
  const confidenceConfig = getConfidenceConfig();

  return (
    <Card className="border-l-4 border-l-orange-500">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge
                variant="outline"
                className="bg-red-50 text-red-700 border-red-200"
              >
                <AlertTriangle className="size-3 mr-1" />
                {decision.severity === 'block' ? 'BLOCKED' : 'WARNING'}
              </Badge>
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                <Clock className="size-3 mr-1" />
                Pending Approval
              </Badge>
              <Badge 
                variant="outline" 
                className={confidenceConfig.color}
              >
                {confidenceConfig.label}
              </Badge>
            </div>
            <CardTitle className="text-lg">{decision.decisionTitle}</CardTitle>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Building2 className="size-4" />
                {decision.companyName}
              </div>
              {decision.engagementName && (
                <div className="flex items-center gap-1">
                  <FileText className="size-4" />
                  {decision.engagementName}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(decision)}
            >
              View Details
            </Button>
            {educationTopic && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2"
                onClick={() => setShowEducation(true)}
              >
                <BookOpen className="size-3 mr-1" />
                Learn more
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Client Selection */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">Client Selected:</p>
          <p className="text-sm text-gray-900">
            <strong>{decision.selectedOption}</strong> service desks
          </p>
        </div>

        {/* Client Justification */}
        {decision.overrideReason && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Client Justification:</p>
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-900">{decision.overrideReason}</p>
            </div>
          </div>
        )}

        {/* Timestamp */}
        <div className="text-xs text-gray-500">
          Submitted {formatDate(decision.evaluatedAt)}
        </div>

        {/* Approval Form */}
        {showApproveForm && (
          <div className="space-y-3 pt-4 border-t">
            <label className="text-sm font-medium text-gray-700">
              Approval Notes (required):
            </label>
            <Textarea
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              placeholder="Document why you're approving this decision despite the warning..."
              rows={3}
              className="resize-none"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleApprove}
                disabled={loading || !approveNotes.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="size-4 mr-2" />
                Confirm Approval
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowApproveForm(false);
                  setApproveNotes('');
                }}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Denial Form */}
        {showDenyForm && (
          <div className="space-y-3 pt-4 border-t">
            <label className="text-sm font-medium text-gray-700">
              Denial Reason (required):
            </label>
            <Textarea
              value={denyNotes}
              onChange={(e) => setDenyNotes(e.target.value)}
              placeholder="Explain why this decision should not be approved and suggest alternatives..."
              rows={3}
              className="resize-none"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleDeny}
                disabled={loading || !denyNotes.trim()}
                variant="destructive"
              >
                <XCircle className="size-4 mr-2" />
                Confirm Denial
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDenyForm(false);
                  setDenyNotes('');
                }}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!showApproveForm && !showDenyForm && (
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => setShowApproveForm(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="size-4 mr-2" />
              Approve
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDenyForm(true)}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <XCircle className="size-4 mr-2" />
              Deny
            </Button>
          </div>
        )}
      </CardContent>

      {educationTopic && educationTopicId && (
        <EducationDrawer
          open={showEducation}
          onClose={() => setShowEducation(false)}
          topicId={educationTopicId}
          topic={educationTopic}
          onDismissTopic={(topicId) => dismissTopic(topicId)}
        />
      )}
    </Card>
  );
}
